import type { Outputs } from "@postdaily/api/routers/index";
import { Badge } from "@postdaily/ui/components/badge";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@postdaily/ui/components/card";
import { Input } from "@postdaily/ui/components/input";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { Textarea } from "@postdaily/ui/components/textarea";
import { cn } from "@postdaily/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { CheckIcon, UndoIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ChannelAvatar } from "@/components/channels/channel-avatar";
import {
  type PreviewAccount,
  type PreviewMedia,
  previewHidesFirstComments,
  resolvePreviewFormat,
} from "@/components/composer/lib/preview-format";
import {
  hasNativePreview,
  nativePreviewWidthClass,
  previewRendersFirstComments,
} from "@/components/composer/lib/preview-layout";
import { providerSupportsThreads } from "@/components/composer/lib/provider-meta";
import { PlatformPreviewCard } from "@/components/composer/platform-preview-card";
import { ThreadPreview } from "@/components/composer/thread-composer";
import { portalTrpcClient } from "@/lib/portal-trpc";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const Route = createFileRoute("/(portal)/review/$token")({
  component: RouteComponent,
});

type PortalReview = Outputs["approvals"]["portal"]["resolve"];
type PortalPost = PortalReview["posts"][number];
type PortalTarget = PortalPost["targets"][number];
type PortalMedia = PortalPost["media"][number];

const portalReviewKey = (token: string) => ["portal", "review", token] as const;

function formatScheduledAt(post: PortalPost) {
  if (!post.scheduledAt) {
    return "Not scheduled yet";
  }

  const zone = post.timezone ?? dayjs.tz.guess();

  return `${dayjs(post.scheduledAt).tz(zone).format("ddd D MMM, h:mm A")} (${zone})`;
}

function channelLabel(target: PortalTarget) {
  return target.displayName ?? target.handle ?? target.provider;
}

/** The portal's flat target row, shaped for the shared preview cards. */
function toPreviewAccount(target: PortalTarget): PreviewAccount {
  return {
    avatarUrl: target.avatarUrl,
    displayName: target.displayName,
    isOrganization: target.isOrganization,
    provider: target.provider,
    providerAccountHandle: target.handle,
  };
}

/**
 * Portal media carries a short-lived signed URL rather than the workspace
 * `previewUrl`, so map it onto the preview shape the cards expect.
 */
function toPreviewMedia(item: PortalMedia): PreviewMedia {
  return {
    height: item.height,
    id: item.id,
    mimeType: item.mimeType,
    previewUrl: item.url,
    width: item.width,
  };
}

/** Plain fallback for channels without a native card (test/mock accounts). */
function PlainPostPreview({
  target,
  media,
}: {
  target: PortalTarget;
  media: PortalMedia[];
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-3">
      <div className="flex items-center gap-2.5">
        <ChannelAvatar
          name={channelLabel(target)}
          avatarUrl={target.avatarUrl}
          provider={target.provider}
          size="sm"
        />
        <span className="truncate font-semibold text-sm">
          {channelLabel(target)}
        </span>
      </div>
      {target.text.trim() ? (
        <p className="wrap-break-word whitespace-pre-wrap text-sm leading-relaxed">
          {target.text}
        </p>
      ) : (
        <p className="text-muted-foreground text-sm italic">No content</p>
      )}
      {media.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {media.map((item) =>
            item.kind === "video" ? (
              // biome-ignore lint/a11y/useMediaCaption: user-uploaded social media has no caption track
              <video
                key={item.id}
                src={item.url}
                controls
                preload="metadata"
                className="aspect-square w-full rounded-lg border object-cover"
              />
            ) : (
              <img
                key={item.id}
                src={item.url}
                alt={item.altText ?? ""}
                loading="lazy"
                className="aspect-square w-full rounded-lg border object-cover"
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the client actually sees: the same native platform card the team saw in
 * the composer, for the selected channel — per-channel copy, format chrome
 * (Reel / Story / Short), and the comments queued behind the post.
 */
function ChannelPreview({
  post,
  target,
}: {
  post: PortalPost;
  target: PortalTarget;
}) {
  const format = resolvePreviewFormat(target.provider, target.previewOptions);
  const media = post.media.map(toPreviewMedia);
  const account = toPreviewAccount(target);

  const isThread =
    post.isThread &&
    Boolean(post.threadSegments?.length) &&
    providerSupportsThreads(target.provider);

  const cardRendersComments = previewRendersFirstComments(target.provider);
  const comments = previewHidesFirstComments(format)
    ? []
    : target.firstComments;

  if (isThread && post.threadSegments) {
    return (
      <ThreadPreview
        segments={post.threadSegments.map((segment, index) => ({
          text: segment.text,
          media: post.media
            .filter((item) => (item.segmentIndex ?? 0) === index)
            .map(toPreviewMedia),
        }))}
        account={account}
        provider={target.provider}
        firstComments={comments.length > 0 ? comments : undefined}
      />
    );
  }

  if (!hasNativePreview(target.provider)) {
    return <PlainPostPreview target={target} media={post.media} />;
  }

  const instagram = target.previewOptions.instagram;
  const tiktok = target.previewOptions.tiktok;

  return (
    <div
      className={cn("w-full", nativePreviewWidthClass(target.provider, format))}
    >
      <PlatformPreviewCard
        provider={target.provider}
        account={account}
        text={target.text}
        media={media}
        format={format}
        linkedin={target.previewOptions.linkedin}
        videoTitle={target.previewOptions.youtube?.title}
        firstComments={
          cardRendersComments && comments.length > 0 ? comments : undefined
        }
        thumbOffsetMs={
          target.provider === "tiktok"
            ? tiktok?.videoCoverTimestampMs
            : instagram?.thumbOffsetMs
        }
        showAiInfo={
          target.provider === "tiktok"
            ? tiktok?.isAigc === true
            : (format === "reel" || format === "story") &&
              instagram?.isAiGenerated === true
        }
        userTags={format === "reel" ? instagram?.userTags : undefined}
        audioName={format === "reel" ? instagram?.audioName : undefined}
      />

      {!cardRendersComments && comments.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1.5">
          {comments.map((comment) => (
            <p
              key={comment}
              className="wrap-break-word whitespace-pre-wrap rounded-xl border bg-card px-3 py-2 text-muted-foreground text-xs leading-relaxed"
            >
              <span className="font-medium text-foreground">First comment</span>
              {": "}
              {comment}
            </p>
          ))}
        </div>
      ) : null}

      {comments.length > 0 ? (
        <span className="mt-1.5 block text-[11px] text-muted-foreground">
          {comments.length === 1
            ? "First comment is posted right after publishing."
            : "Comments are posted right after publishing."}
        </span>
      ) : null}
    </div>
  );
}

function ReviewCard({
  post,
  token,
  reviewerName,
}: {
  post: PortalPost;
  token: string;
  reviewerName: string;
}) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (verdict: "approved" | "changes_requested") =>
      portalTrpcClient.approvals.portal.decide.mutate({
        comment: comment.trim() || undefined,
        postId: post.postId,
        reviewerName: reviewerName.trim() || undefined,
        token,
        verdict,
      }),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async (result) => {
      toast.success(
        result.approvalStatus === "approved"
          ? "Approved — thank you!"
          : "Changes requested — the team has been notified.",
      );
      setComment("");
      await queryClient.invalidateQueries({ queryKey: portalReviewKey(token) });
    },
  });

  // One mutation backs both verdicts, so spin only the pressed button.
  const decidingVerdict = decide.isPending ? decide.variables : null;

  const activeTarget =
    post.targets.find((target) => target.id === activeTargetId) ??
    post.targets[0] ??
    null;

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {post.targets.map((target) => {
            const active = target.id === activeTarget?.id;
            const single = post.targets.length === 1;
            return (
              <button
                key={target.id}
                type="button"
                disabled={single}
                aria-pressed={single ? undefined : active}
                onClick={() => setActiveTargetId(target.id)}
                className={cn(
                  "flex items-center gap-2 rounded-full border px-2 py-1 transition-colors",
                  single
                    ? "cursor-default border-transparent"
                    : active
                      ? "border-primary/40 bg-primary/5"
                      : "border-transparent hover:bg-muted/60",
                )}
              >
                <ChannelAvatar
                  name={channelLabel(target)}
                  avatarUrl={target.avatarUrl}
                  provider={target.provider}
                  size="sm"
                />
                <span className="text-sm">{channelLabel(target)}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {post.approvalStatus === "changes_requested" ? (
            <Badge
              variant="outline"
              className="border-destructive/30 bg-destructive/10 text-destructive"
            >
              Changes requested
            </Badge>
          ) : null}
          <span className="text-muted-foreground text-xs">
            {formatScheduledAt(post)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {activeTarget ? (
          <ChannelPreview post={post} target={activeTarget} />
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {post.baseText}
          </p>
        )}

        {post.latestDecision?.comment ? (
          <p className="rounded-lg border bg-muted/50 p-2.5 text-muted-foreground text-xs">
            Your last note: “{post.latestDecision.comment}”
          </p>
        ) : null}

        <Textarea
          value={comment}
          rows={2}
          maxLength={2000}
          placeholder="Add a note (optional)…"
          onChange={(event) => setComment(event.target.value)}
          aria-label="Add a note (optional)"
        />
      </CardContent>

      <CardFooter className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={decide.isPending}
          onClick={() => decide.mutate("approved")}
        >
          {decidingVerdict === "approved" ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <CheckIcon data-icon="inline-start" />
          )}
          Approve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={decide.isPending}
          onClick={() => decide.mutate("changes_requested")}
        >
          {decidingVerdict === "changes_requested" ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <UndoIcon data-icon="inline-start" />
          )}
          Request changes
        </Button>
      </CardFooter>
    </Card>
  );
}

function RouteComponent() {
  const { token } = Route.useParams();
  const [reviewerName, setReviewerName] = useState("");

  const reviewQuery = useQuery({
    queryFn: () => portalTrpcClient.approvals.portal.resolve.query({ token }),
    queryKey: portalReviewKey(token),
    retry: false,
  });

  if (reviewQuery.isPending) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4 sm:p-8">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </main>
    );
  }

  if (reviewQuery.isError) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-3 p-4 sm:p-8">
        <h1 className="font-semibold text-xl">This link isn’t available</h1>
        <p className="text-muted-foreground text-sm">
          It may have been revoked or replaced. Ask the person who shared it for
          a fresh review link.
        </p>
      </main>
    );
  }

  const review = reviewQuery.data;

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4 sm:p-8">
      <header className="flex flex-col gap-1.5">
        <h1 className="font-semibold text-xl">
          Posts from {review.workspaceName}
        </h1>
        <p className="text-muted-foreground text-sm">
          {review.posts.length === 0
            ? "Nothing is waiting for your review right now."
            : `${review.posts.length} post${
                review.posts.length === 1 ? "" : "s"
              } waiting for your approval. Approve to let them publish as scheduled.`}
        </p>
      </header>

      {review.posts.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="portal-reviewer-name" className="text-sm">
            Your name (optional)
          </label>
          <Input
            id="portal-reviewer-name"
            value={reviewerName}
            maxLength={80}
            placeholder="So the team knows who reviewed"
            onChange={(event) => setReviewerName(event.target.value)}
          />
        </div>
      ) : null}

      {review.posts.map((post) => (
        <ReviewCard
          key={post.postId}
          post={post}
          token={token}
          reviewerName={reviewerName}
        />
      ))}
    </main>
  );
}
